import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// Не из индекса фичи: индекс тянет урок и упражнения. Главной нужны только данные тем и правила наград.
import { isReadHere, readLastSlug } from '@features/Grammar/model/marks';
import { continueTarget, homeTopic, rewardCycle } from '@features/Grammar/model/path';
import { useGrammarTopicsQuery } from '@features/Grammar/model/queries';
import { PixelChestIcon } from '@features/Grammar/ui/PixelChestIcon';
import { trackEvent } from '@shared/analytics';
import { buildGrammarExercisePath, buildGrammarLessonPath, paths } from '@shared/config';
import { cn } from '@shared/lib';
import { Button, TokenIcon } from '@shared/ui';

/**
 * «Грамматика» на главной (решение владельца 2026-10-25): темы проходят в любом порядке, поэтому главное — сколько тем
 * осталось до награды (+2 несгораемых за каждые 5), шкала круга и «Продолжить» — последний открытый незасчитанный урок
 * или первая незасчитанная тема пути. Справа под «На сегодня» (уже 880 px колонки — над «Встретилось новое слово?»);
 * тем у ученика нет — плашки нет.
 */
export function GrammarCard({ phone }: { phone: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const data = useGrammarTopicsQuery().data;
  const topics = data?.topics ?? [];
  // Та же тема, что «ты здесь» и «Продолжить» на тропинке: одна функция `homeTopic` (2026-10-26).
  const next = useMemo(() => homeTopic(topics, readLastSlug()), [topics]);
  if (!data || topics.length === 0) return null;

  const rewards = data.rewards;
  const cycle = rewards ? rewardCycle(rewards) : null;
  const title = !next ? t('dashboard.home.grammar.allDone') : cycle ? t('dashboard.home.grammar.left', { count: cycle.left }) : t('dashboard.home.grammar.plain');

  const openTopic = () => {
    if (!next) return;
    trackEvent('grammar_topic_opened', { slug: next.slug, place: 'home' });
    // Урок дочитан, а тема не пройдена — сразу в упражнение 1 (grammar-completion-tz § 3.4); текст кнопки тот же.
    navigate(
      continueTarget(next, isReadHere(next.slug)) === 'exercise' ? buildGrammarExercisePath(next.slug, 1) : buildGrammarLessonPath(next.slug),
    );
  };

  return (
    <section
      aria-labelledby="dash-grammar-title"
      className={cn('dash-grammar flex flex-col rounded-card border border-solid border-[#f2c14e]/30 bg-card', phone ? 'gap-3 p-4' : 'gap-3 px-6 py-5')}
    >
      <div className="flex items-center gap-3">
        <span aria-hidden className="lx-px-sm grid h-11 w-11 flex-none place-items-center bg-[#2a2116]">
          <PixelChestIcon className="h-8 w-8" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-[12px] font-semibold uppercase leading-4 tracking-[0.14em] text-muted">{t('dashboard.home.grammar.kicker')}</p>
            <button
              type="button"
              onClick={() => {
                trackEvent('home_grammar_clicked', { action: 'all' });
                navigate(paths.grammar);
              }}
              className="flex-none text-[13px] font-semibold text-fg hover:text-accent"
            >
              {t('dashboard.home.grammar.all')} →
            </button>
          </div>
          <h2 id="dash-grammar-title" className="mt-1 font-serif text-[19px] font-medium leading-6 text-fg">
            {title}
          </h2>
        </div>
      </div>
      {cycle && next && rewards && (
        <div className="flex items-center gap-2" aria-label={t('dashboard.home.grammar.ofStep', { done: cycle.done, step: cycle.step })}>
          <div className="flex flex-1 gap-1.5" aria-hidden>
            {Array.from({ length: cycle.step }, (_, i) => (
              <span key={i} className={cn('h-2.5 flex-1 rounded-full', i < cycle.done ? 'bg-accent' : 'bg-white/[0.1]')} />
            ))}
          </div>
          <span className="inline-flex flex-none items-center gap-1 rounded-pill bg-[#f2c14e]/15 px-2.5 py-0.5 text-[13px] font-semibold text-[#f2c14e]">
            <TokenIcon size={14} />
            {t('dashboard.home.grammar.reward', { count: rewards.step_tokens })}
          </span>
        </div>
      )}
      <p className="text-[13px] leading-[18px] text-muted">{t('dashboard.home.grammar.hint')}</p>
      {next && (
        <Button variant="outline" size="sm" onClick={openTopic} className="mt-auto w-full min-w-0">
          <span className="truncate">
            {t(next.progress.opened ? 'dashboard.home.grammar.continue' : 'dashboard.home.grammar.start', { title: next.title })}
          </span>
          <span aria-hidden>→</span>
        </Button>
      )}
    </section>
  );
}
